/**
 * Known dates are a form, not an estimate.
 *
 * The engine does not project a registration date from an invented pendency figure, so a
 * deadline whose base date has not been supplied renders as a prompt for that date rather than
 * as a number. The filing date is the one value the client adjusts — it clamps into the window
 * the server sent, and says it has.
 */

import { Field, DateInput } from '~/ui/Field';
import { Callout } from '~/ui/Feedback';
import { fmtDate } from '~/lib/format';
import type { DateParams } from '~/lib/routes';
import type { FilingWindow, MatterFloor } from '~/api/schema';
import styles from './Matter.module.css';

/** The base dates a deadline can be counted from, beyond the filing itself. */
export const OPTIONAL_DATES = [
  { key: 'priority', label: 'Priority' },
  { key: 'publication', label: 'Published' },
  { key: 'registration', label: 'Registered' },
  { key: 'grant', label: 'Granted' },
] as const;

export const dateFieldId = (key: string) => `known-date-${key}`;

export interface KnownDatesBarProps {
  matterLabel: string;
  filing: string;
  filingClamped: boolean;
  filingWindow: FilingWindow | null;
  floor: MatterFloor;
  dates: DateParams;
  onChange: (patch: Partial<Record<string, string | undefined>>) => void;
  action?: React.ReactNode;
}

export function KnownDatesBar({
  matterLabel,
  filing,
  filingClamped,
  filingWindow,
  floor,
  dates,
  onChange,
  action,
}: KnownDatesBarProps) {
  return (
    <>
      <div className={styles.datesBar}>
        <Field label="Filed on" grow={0}>
          <DateInput
            id={dateFieldId('filing')}
            value={filing}
            {...(filingWindow?.min ? { min: filingWindow.min } : {})}
            {...(filingWindow?.max ? { max: filingWindow.max } : {})}
            onChange={(next) => onChange({ filing: next })}
          />
        </Field>

        {OPTIONAL_DATES.map((entry) => (
          <Field key={entry.key} label={entry.label} grow={0}>
            <DateInput
              id={dateFieldId(entry.key)}
              value={dates[entry.key] ?? ''}
              onChange={(next) => onChange({ [entry.key]: next })}
            />
          </Field>
        ))}

        <span className={styles.datesGrow} />
        {action}
      </div>

      <p className={styles.filingHint}>
        Every {matterLabel.toLowerCase()} rule on this screen is read against the filing date: the
        version that governs it is the one in force on that day. The other dates are supplied, never
        estimated — a deadline counted from one the engine has not been given asks for it rather than
        guessing.
        {filingWindow ? (
          <>
            {' '}
            This register can answer for filings between {fmtDate(filingWindow.min)} and{' '}
            {filingWindow.max ? fmtDate(filingWindow.max) : 'today'}.
          </>
        ) : null}
      </p>

      {filingClamped ? (
        <Callout tone="warn" title="The filing date was moved">
          Showing {fmtDate(filing)}, the earliest filing this register can answer for. The date you
          asked about falls outside the window in which any rule constituting the right was in
          force, so the engine has nothing to apply to it.
        </Callout>
      ) : null}

      {floor.calendarNote ? (
        <Callout tone="neutral" title="A bound with a calendar question behind it">
          <p>{floor.calendarNote}</p>
          {floor.note ? <p>{floor.note}</p> : null}
          {floor.citation ? <p>Authority: {floor.citation}</p> : null}
          {floor.owner ? <p>Stood behind by: {floor.owner}</p> : null}
        </Callout>
      ) : null}
    </>
  );
}

/** "Needs the registration date", focusing the field it names. */
export function NeedsDatePrompt({ basis, label }: { basis: string; label: string }) {
  return (
    <button
      type="button"
      className={styles.resultNone}
      style={{ border: 0, background: 'none', cursor: 'pointer', font: 'inherit' }}
      onClick={() => document.getElementById(dateFieldId(basis))?.focus()}
    >
      Needs the {label.toLowerCase()} date
    </button>
  );
}
